import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { hostname } from "../../../utils";

interface Site {
  id: string;
  displayName: string;
  shortName: string;
  previewUrl?: string;
}

interface Collection {
  id: string;
  displayName: string;
  singularName: string;
  slug: string;
}

interface WebflowIntegrationFormProps {
  editMode: boolean;
  initialData?: {
    apiToken?: string;
    siteId?: string;
    siteName?: string;
    collectionId?: string;
    collectionName?: string;
  };
  companyId: string;
  instanceName: string;
  getToken: () => Promise<string>;
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: any) => void;
}

/**
 * Webflow CMS integration form.
 *
 * Uses Webflow API token for authentication, then allows users to select:
 * 1. A Webflow site
 * 2. A CMS collection to publish content to
 */
export default function WebflowIntegrationForm({
  editMode,
  initialData = {},
  companyId,
  instanceName,
  getToken,
  onValidationChange,
  onDataChange,
}: WebflowIntegrationFormProps) {
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [isLoadingCollections, setIsLoadingCollections] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [error, setError] = useState("");
  const [isConnected, setIsConnected] = useState(false);

  // API Token
  const [apiToken, setApiToken] = useState(initialData.apiToken || "");

  // Site and collection selection
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<Collection | null>(null);

  // Check if already connected (edit mode)
  useEffect(() => {
    if (editMode && initialData.apiToken) {
      setApiToken(initialData.apiToken);
      setIsConnected(true);
      // Set initial selections if available
      if (initialData.siteId) {
        setSelectedSite({
          id: initialData.siteId,
          displayName: initialData.siteName || "",
          shortName: "",
        });
      }
      if (initialData.collectionId) {
        setSelectedCollection({
          id: initialData.collectionId,
          displayName: initialData.collectionName || "",
          singularName: "",
          slug: "",
        });
      }
    }
  }, [editMode, initialData]);

  // Form is valid when connected and a collection is selected
  const isFormValid = isConnected && selectedCollection !== null && apiToken.length > 0;

  // Update parent when state changes
  useEffect(() => {
    onValidationChange(isFormValid);
    onDataChange({
      connected: isConnected,
      apiToken,
      selectedSite,
      selectedCollection,
      siteId: selectedSite?.id,
      siteName: selectedSite?.displayName,
      collectionId: selectedCollection?.id,
      collectionName: selectedCollection?.displayName,
    });
  }, [isFormValid, isConnected, apiToken, selectedSite, selectedCollection, onValidationChange, onDataChange]);

  // Test connection and fetch sites
  const testConnection = useCallback(async () => {
    if (!apiToken.trim()) {
      setError("Please enter your Webflow API token");
      return;
    }

    setIsTestingConnection(true);
    setError("");
    setSites([]);
    setSelectedSite(null);
    setCollections([]);
    setSelectedCollection(null);

    try {
      const token = await getToken();

      // Test the API token by fetching sites
      const response = await axios.post(
        `${hostname}/integrations/webflow/test-token`,
        {
          apiToken: apiToken.trim(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setIsConnected(true);
        if (response.data.sites) {
          setSites(response.data.sites);
          // Auto-select if only one site
          if (response.data.sites.length === 1) {
            setSelectedSite(response.data.sites[0]);
          }
        }
      } else {
        setError(response.data.error || "Failed to connect to Webflow");
      }
    } catch (err: any) {
      console.error("Error testing Webflow connection:", err);
      setError(
        err.response?.data?.error ||
          "Failed to connect to Webflow. Please check your API token."
      );
    } finally {
      setIsTestingConnection(false);
    }
  }, [apiToken, getToken]);

  // Fetch collections when site is selected
  const fetchCollections = useCallback(
    async (siteId: string) => {
      setIsLoadingCollections(true);
      setError("");
      setCollections([]);
      setSelectedCollection(null);

      try {
        const token = await getToken();
        const response = await axios.post(
          `${hostname}/integrations/webflow/collections`,
          {
            apiToken: apiToken.trim(),
            siteId,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data.collections) {
          setCollections(response.data.collections);
          // Auto-select if only one collection
          if (response.data.collections.length === 1) {
            setSelectedCollection(response.data.collections[0]);
          }
        }
      } catch (err: any) {
        console.error("Error fetching collections:", err);
        setError(
          err.response?.data?.error ||
            "Failed to fetch collections. Please try again."
        );
      } finally {
        setIsLoadingCollections(false);
      }
    },
    [getToken, apiToken]
  );

  // Fetch collections when site changes
  useEffect(() => {
    if (isConnected && selectedSite && apiToken) {
      fetchCollections(selectedSite.id);
    }
  }, [selectedSite, isConnected, apiToken, fetchCollections]);

  // In edit mode with existing connection, show connected status
  if (editMode && isConnected && initialData.collectionId) {
    return (
      <Box>
        <Alert severity="success" sx={{ mb: 2 }}>
          Webflow account connected
          {initialData.siteName && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Site: {initialData.siteName}
            </Typography>
          )}
          {initialData.collectionName && (
            <Typography variant="caption" display="block">
              Collection: {initialData.collectionName}
            </Typography>
          )}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          To change the connected account, update the API token below and test the connection.
        </Typography>
        <TextField
          fullWidth
          label="Webflow API Token"
          type="password"
          value={apiToken}
          onChange={(e) => {
            setApiToken(e.target.value);
            setIsConnected(false);
          }}
          sx={{ mt: 2 }}
          helperText="Enter a new token to reconnect"
        />
        {!isConnected && apiToken !== initialData.apiToken && (
          <Button
            variant="outlined"
            onClick={testConnection}
            disabled={isTestingConnection || !apiToken.trim()}
            sx={{ mt: 1 }}
          >
            {isTestingConnection ? "Testing..." : "Test Connection"}
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Connect your Webflow account using a Site API token to publish content
          directly to your CMS collections.
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          <Link
            href="https://university.webflow.com/lesson/intro-to-the-webflow-api#how-to-generate-an-api-access-token"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn how to create a Webflow API token
          </Link>
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!isConnected ? (
        <>
          <TextField
            fullWidth
            label="Webflow API Token"
            type="password"
            value={apiToken}
            onChange={(e) => setApiToken(e.target.value)}
            placeholder="Enter your Webflow Site API token"
            sx={{ mb: 2 }}
            helperText="You can find this in your Webflow site settings under 'Integrations'"
          />

          <Button
            variant="contained"
            onClick={testConnection}
            disabled={isTestingConnection || !apiToken.trim()}
            startIcon={
              isTestingConnection && <CircularProgress size={16} color="inherit" />
            }
            fullWidth
            size="large"
            sx={{ mb: 2 }}
          >
            {isTestingConnection ? "Connecting..." : "Connect to Webflow"}
          </Button>
        </>
      ) : (
        <>
          <Alert severity="success" sx={{ mb: 2 }}>
            Webflow connected successfully! Now select a site and collection.
          </Alert>

          {/* Site Selection */}
          <Box sx={{ mt: 2 }}>
            {isLoadingSites ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
                <CircularProgress size={20} />
                <Typography>Loading sites...</Typography>
              </Box>
            ) : sites.length > 0 ? (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="site-select-label">Select Site</InputLabel>
                <Select
                  labelId="site-select-label"
                  value={selectedSite?.id || ""}
                  label="Select Site"
                  onChange={(e) => {
                    const site = sites.find((s) => s.id === e.target.value);
                    setSelectedSite(site || null);
                  }}
                >
                  {sites.map((site) => (
                    <MenuItem key={site.id} value={site.id}>
                      {site.displayName}
                      {site.shortName && ` (${site.shortName})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No Webflow sites found. Make sure your API token has access to at least one site.
              </Alert>
            )}
          </Box>

          {/* Collection Selection */}
          {selectedSite && (
            <Box sx={{ mt: 2 }}>
              {isLoadingCollections ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
                  <CircularProgress size={20} />
                  <Typography>Loading collections...</Typography>
                </Box>
              ) : collections.length > 0 ? (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="collection-select-label">
                    Select Collection
                  </InputLabel>
                  <Select
                    labelId="collection-select-label"
                    value={selectedCollection?.id || ""}
                    label="Select Collection"
                    onChange={(e) => {
                      const collection = collections.find(
                        (c) => c.id === e.target.value
                      );
                      setSelectedCollection(collection || null);
                    }}
                  >
                    {collections.map((collection) => (
                      <MenuItem key={collection.id} value={collection.id}>
                        {collection.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No CMS collections found for this site. Create a CMS collection
                  in Webflow first.
                </Alert>
              )}
            </Box>
          )}

          {/* Show selected configuration */}
          {selectedCollection && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Content will be published to:{" "}
                <strong>
                  {selectedSite?.displayName} / {selectedCollection.displayName}
                </strong>
                <br />
                Click "Install" to save this integration.
              </Typography>
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}
